import { useMemo } from "react";
import LazyApexChart from "../../../client/components/LazyApexChart";
import type { ApexOptions } from "apexcharts";
import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { chartColors, pnlColor } from "../chart/chartColors";
import ChartViewToggle from "../ChartViewToggle";
import WidgetDataTable from "../WidgetDataTable";
import { isTradeInSession } from "../../../trading-sessions/sessionUtils";
import type { WidgetViewProps } from "../types";

function fmt(v: number): string {
  const abs = Math.abs(v);
  const prefix = v < 0 ? "-$" : "$";
  if (abs >= 1000) return `${prefix}${(abs / 1000).toFixed(1)}k`;
  return `${prefix}${abs.toFixed(0)}`;
}

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function computeSessionPnl(session: any, trades: any[]): number {
  if (!session.startedAt || !session.endedAt) return 0;
  // Attach trades to the session by their ENTRY time (see isTradeInSession),
  // identical to SessionsListWidget so both widgets agree on per-session PnL.
  const sessionTrades = trades.filter((tr) => isTradeInSession(tr, session));
  const pnl = sessionTrades.reduce(
    (sum, tr) => sum + (tr.pnl ?? 0) + (tr.commission ?? 0) + (tr.swaps ?? 0),
    0,
  );
  return Math.round(pnl * 100) / 100;
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

export default function SessionPnlChartWidget({ viewMode = "chart", onSetViewMode }: WidgetViewProps) {
  const { sessions, statsTradesList, isDark, themeId, t } = useDashboardData();

  // One entry per completed session, oldest → newest (left to right on the chart).
  const rows = useMemo(() => {
    const completed = (sessions ?? [])
      .filter((s: any) => !s.isActive && s.startedAt && s.endedAt)
      .slice()
      .sort(
        (a: any, b: any) =>
          new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
      );
    return completed.map((s: any) => ({
      label: s.name || formatDate(s.startedAt),
      pnl: computeSessionPnl(s, statsTradesList ?? []),
    }));
  }, [sessions, statsTradesList]);

  const barColors = rows.map((r) => pnlColor(r.pnl));

  const c = chartColors();
  const tooltipTheme = isDark ? "dark" : "light";
  const axisLabelStyle = { colors: c.axis, fontSize: "11px" };

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar" as const,
        toolbar: { show: false },
        background: "transparent",
        animations: { enabled: false },
      },
      plotOptions: {
        bar: { horizontal: false, borderRadius: 4, columnWidth: "60%", distributed: true },
      },
      colors: barColors,
      dataLabels: { enabled: false },
      grid: {
        borderColor: c.grid,
        strokeDashArray: 3,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      xaxis: {
        categories: rows.map((r) => r.label),
        labels: { style: axisLabelStyle, rotate: -45, hideOverlappingLabels: true, trim: true },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: axisLabelStyle, formatter: fmt as any } },
      tooltip: { theme: tooltipTheme, y: { formatter: fmt as any } },
      legend: { show: false },
      theme: { mode: isDark ? ("dark" as const) : ("light" as const) },
    }),
    [isDark, rows, barColors, c.grid, tooltipTheme],
  );

  const tableColumns = [
    { key: "label", label: t.analytics.sessionsListName, align: "left" as const },
    { key: "pnl", label: t.analytics.sessionsListPnl, align: "right" as const },
  ];
  const tableRows = rows.map((r) => ({
    label: r.label,
    pnl: (
      <span className={r.pnl > 0 ? "text-pnlPositive" : r.pnl < 0 ? "text-pnlNegative" : "text-muted-foreground"}>
        {r.pnl !== 0 ? fmt(r.pnl) : "—"}
      </span>
    ),
  }));

  if (!sessions || !statsTradesList) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader label={t.analytics.sessionPnlChartLabel} isDark={isDark} />
        <CardContent className="h-full p-4">
          <EmptyState label={t.analytics.noDataAvailable} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.analytics.sessionPnlChartLabel}
        isDark={isDark}
        rightContent={<ChartViewToggle value={viewMode} onChange={onSetViewMode} />}
      />
      <CardContent className="flex-1 px-3 pb-2 pt-0 min-h-0">
        {rows.length === 0 ? (
          <EmptyState label={t.analytics.sessionsListNoSessions} />
        ) : viewMode === "table" ? (
          <WidgetDataTable columns={tableColumns} rows={tableRows} emptyLabel={t.analytics.sessionsListNoSessions} />
        ) : (
          <LazyApexChart
            key={`session-pnl-${themeId}`}
            options={options}
            series={[{ name: t.analytics.sessionsListPnl, data: rows.map((r) => r.pnl) }]}
            type="bar"
            height="100%"
          />
        )}
      </CardContent>
    </Card>
  );
}
